import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Box,
  CircularProgress, Typography, Tooltip, IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const Roles = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "Params", "create", false);
  const canEdit = hasRight(permissionMap, "Params", "update", false);
  const canDelete = hasRight(permissionMap, "Params", "delete", false);

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const handleRoles = async () => {
      try {
        setError(null);
        setLoading(true);
        const res = await api.get('/roles');
        setRoles(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handleRoles();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(isMobile ? { width: 60 } : { flex: 1 }),
    },
    {
      field: "role",
      headerName: "Role",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
    },
    {
      field: "description",
      headerName: "Description",
      ...(isMobile ? { width: 200 } : { flex: 3 }),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];
    const baseWidth = 60 + activeCount * 25;

    return [{
      field: "action",
      headerName: "Action",
      filterable: false,
      sortable: false,
      width: baseWidth,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          sx={{ width: '100%', height: '100%' }}
        >
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: theme.palette.primary.dark,
                  '&:hover': { color: theme.palette.green.deep },
                }}
                onClick={() => navigate(`/roles/${params.row._id}`)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: theme.palette.primary.dark,
                  '&:hover': { color: theme.palette.green.deep },
                }}
                onClick={() => handleDeleteClick(params.row._id)}
              >
                <DeleteForever />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    }];
  }, [canEdit, canDelete, theme, navigate]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, roles);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/roles/delete/${idToDelete}`);
        setRoles(roles.filter((item) => item._id !== idToDelete));
        toast.success('Role deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, roles);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/roles/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setRoles(roles.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error("Failed to delete: " + err.message);
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, roles]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Roles"
          subtitle={`${loading ? 'Loading...' : 'List of Roles'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, roles)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/roles/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">
            Error: {error?.message || ''}
          </Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CardList
                rows={roles}
                primaryField="role"
                fields={[
                  { label: "Description", key: "description" },
                ]}
                isActiveField="isActive"
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/roles/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={roles || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Roles;
